import { toast } from "sonner";
import { Copy } from "lucide-react";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { ASSETS, BRAND, PRESS_KIT_BIOS } from "../data/siteContent";
import { PRESSKIT_T } from "../constants/siteTestIds";
import { Seo } from "../seo/Seo";
import { buildSchemas, webPageSchema, breadcrumbSchema } from "../seo/schemas";

const copy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied: ${label}`);
  } catch {
    toast.error("Could not copy.");
  }
};

const BioBlock = ({ length, label, body, tid, words }) => (
  <div className="border-t border-black/10 pt-8">
    <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
      <div>
        <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Bio · {label}</p>
        <p className="mt-1 font-mono-ui text-[10px] tracking-[0.25em] text-black/35">~ {words} words</p>
      </div>
      <button
        data-testid={tid}
        onClick={() => copy(body, label)}
        className="inline-flex items-center gap-2 border border-black/25 hover:bg-[#0a0a0a] hover:text-white px-4 py-2.5 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition-all"
      >
        <Copy size={12} /> Copy
      </button>
    </div>
    <p className="text-black/75 text-base md:text-lg leading-relaxed">{body}</p>
  </div>
);

export default function PressKit() {
  return (
    <SiteLayout>
      <Seo
        title="Press Kit — Manuel Santana"
        description="Manuel Santana press kit. Pre-written bios at 50, 100 and 250 words, headshots, logos and press contact information for journalists and podcast hosts."
        path="/press-kit"
        schemas={buildSchemas([
          webPageSchema({ path: "/press-kit", name: "Manuel Santana Press Kit", description: "Bios, headshots, logos and press contact for journalists." }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Press Kit", path: "/press-kit" }]),
        ])}
      />
      <PageHeader
        overline="Press Kit"
        title="Everything"
        italic="journalists need."
        kicker="Pre-written bios at three lengths, headshots, logos and contact info. All free to use. For interview booking, see the Contact page."
      />

      <section data-testid={PRESSKIT_T.section} className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid grid-cols-12 gap-8 lg:gap-16">
          <aside className="col-span-12 lg:col-span-5 space-y-8">
            <div>
              <div className="portrait-frame w-full max-w-[560px] aspect-[4/5]">
                <img src={ASSETS.heroPortrait} alt="Manuel Santana headshot 1" draggable={false} />
              </div>
              <p className="mt-3 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Headshot 01</p>
            </div>
            <div>
              <div className="portrait-frame w-full max-w-[560px] aspect-[4/5]">
                <img src={ASSETS.aboutPortrait} alt="Manuel Santana headshot 2" draggable={false} />
              </div>
              <p className="mt-3 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Headshot 02</p>
            </div>
            <div>
              <div className="portrait-frame w-full max-w-[560px] aspect-[4/5]">
                <img src={ASSETS.podcastPortrait} alt="Manuel Santana headshot 3" draggable={false} />
              </div>
              <p className="mt-3 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Headshot 03 · Podcast</p>
            </div>
          </aside>

          <div className="col-span-12 lg:col-span-7 space-y-12">
            <BioBlock length="short" label="Short" tid={PRESSKIT_T.downloadShort} body={PRESS_KIT_BIOS.short} words={50} />
            <BioBlock length="medium" label="Medium" tid={PRESSKIT_T.downloadMedium} body={PRESS_KIT_BIOS.medium} words={100} />
            <BioBlock length="long" label="Long" tid={PRESSKIT_T.downloadLong} body={PRESS_KIT_BIOS.long} words={250} />

            <div className="border-t border-black/10 pt-8">
              <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">— Show & Brand Logos</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-black/15 px-6 py-10 text-center">
                  <p className="font-display text-2xl text-[#0a0a0a]">The Prove<br/>Your Point<br/>Podcast</p>
                </div>
                <div className="border border-black/15 px-6 py-10 text-center">
                  <p className="font-display text-2xl text-[#0a0a0a]">Manuel<br/>Santana</p>
                </div>
              </div>
              <p className="mt-4 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-black/40">High-res .png and .svg available on request via media@iammanuelsantana.com</p>
            </div>

            <div className="border-t border-black/10 pt-8">
              <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">— Press Contact</p>
              <p className="text-black/75 text-base">Manuel Santana</p>
              <p className="text-black/60 text-sm">{BRAND.emails.press}</p>
              <p className="text-black/60 text-sm">{BRAND.cities.join(" · ")}</p>
              <p className="text-black/60 text-sm">{BRAND.domain}</p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
